import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api, { getUploadUrl } from '../utils/api';
import { getUser, logout } from '../utils/auth';
import { FileText, LogOut, Plus, Building2, Clock, AlertCircle, Pencil, Trash2 } from 'lucide-react';
import { toast } from 'sonner';

const DataEntryDashboard = () => {
  const navigate = useNavigate();
  const user = getUser();
  const [school, setSchool] = useState(null);
  const [exams, setExams] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchData = async () => {
    try {
      const [schoolRes, examsRes] = await Promise.all([
        api.get('/school/info'),
        api.get('/exams')
      ]);
      setSchool(schoolRes.data);
      // Filter to only show draft exams created by this user
      setExams(examsRes.data.filter(e => e.teacher_id === user.id));
    } catch (err) {
      console.error('Failed to fetch data:', err);
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!user || user.role !== 'data_entry_operator') {
      navigate('/');
      return;
    }
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, navigate]);

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const deleteExam = async (examId, examTitle) => {
    if (!window.confirm(`Are you sure you want to delete "${examTitle}"? This action cannot be undone.`)) {
      return;
    }
    const loadingToast = toast.loading('Deleting exam...');
    try {
      await api.delete(`/exams/${examId}`);
      toast.dismiss(loadingToast);
      toast.success('Exam deleted successfully!');
      fetchData();
    } catch (err) {
      toast.dismiss(loadingToast);
      toast.error(err.response?.data?.detail || 'Failed to delete exam');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="text-slate-600">Loading...</div>
      </div>
    );
  }

  const draftExams = exams.filter(e => e.status === 'draft');

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header with School Branding */}
      <header className="bg-white border-b border-slate-200 px-6 py-4">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-4">
            {school?.logo_url ? (
              <img src={getUploadUrl(school.logo_url)} alt={school.name} className="w-12 h-12 rounded-lg object-cover" />
            ) : (
              <div className="w-12 h-12 rounded-lg bg-amber-100 flex items-center justify-center">
                <Building2 className="w-6 h-6 text-amber-700" />
              </div>
            )}
            <div>
              <h1 className="text-xl font-bold text-slate-900">{school?.name || 'Data Entry Dashboard'}</h1>
              <p className="text-sm text-slate-600">Welcome, {user?.name}</p>
            </div>
          </div>
          <button
            onClick={handleLogout}
            className="flex items-center gap-2 px-4 py-2 text-slate-600 hover:text-slate-900 hover:bg-slate-100 rounded-lg transition-all"
          >
            <LogOut className="w-5 h-5" />
            Logout
          </button>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-8">
        {/* Info Banner */}
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 mb-8 flex items-start gap-3">
          <AlertCircle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
          <div>
            <h3 className="font-medium text-amber-800">Data Entry Operator</h3>
            <p className="text-sm text-amber-700">
              You can create exams as drafts. Only teachers or school admins can activate exams for students.
            </p>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
          <div className="bg-white p-6 rounded-xl border border-slate-200">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-amber-100 rounded-lg">
                <FileText className="w-6 h-6 text-amber-700" />
              </div>
              <div>
                <p className="text-sm text-slate-500">Draft Exams Created</p>
                <p className="text-2xl font-bold text-slate-900">{draftExams.length}</p>
              </div>
            </div>
          </div>
          <div className="bg-white p-6 rounded-xl border border-slate-200">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-slate-100 rounded-lg">
                <FileText className="w-6 h-6 text-slate-600" />
              </div>
              <div>
                <p className="text-sm text-slate-500">Total Exams</p>
                <p className="text-2xl font-bold text-slate-900">{exams.length}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Create New Exam Button */}
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-xl font-bold text-slate-900">My Draft Exams</h2>
          <button
            onClick={() => navigate('/data-entry/create-exam')}
            className="flex items-center gap-2 px-4 py-2 bg-amber-600 hover:bg-amber-700 text-white rounded-lg transition-all"
          >
            <Plus className="w-5 h-5" />
            Create New Exam (Draft)
          </button>
        </div>

        {/* Exams List */}
        {draftExams.length === 0 ? (
          <div className="bg-white rounded-xl border border-slate-200 p-12 text-center">
            <FileText className="w-12 h-12 text-slate-300 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-slate-900 mb-2">No Draft Exams</h3>
            <p className="text-slate-500 mb-4">Create your first exam to get started</p>
            <button
              onClick={() => navigate('/data-entry/create-exam')}
              className="px-4 py-2 bg-amber-600 hover:bg-amber-700 text-white rounded-lg transition-all"
            >
              Create Exam
            </button>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {draftExams.map(exam => (
              <div key={exam.id} className="bg-white rounded-xl border border-slate-200 p-6">
                <div className="flex items-start justify-between mb-4">
                  <h3 className="font-semibold text-slate-900">{exam.title}</h3>
                  <span className="px-2 py-1 bg-amber-100 text-amber-700 rounded text-xs font-medium">
                    Draft
                  </span>
                </div>
                
                <div className="space-y-2 text-sm text-slate-600 mb-4">
                  <div className="flex items-center gap-2">
                    <FileText className="w-4 h-4" />
                    <span>{exam.questions?.length || 0} questions</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Clock className="w-4 h-4" />
                    <span>{exam.duration_minutes} minutes</span>
                  </div>
                </div>

                {exam.board_name && exam.grade_name && (
                  <div className="flex flex-wrap gap-1 mb-4">
                    <span className="px-2 py-0.5 bg-slate-100 text-slate-600 rounded text-xs">{exam.board_name}</span>
                    <span className="px-2 py-0.5 bg-slate-100 text-slate-600 rounded text-xs">{exam.grade_name}</span>
                    {exam.subject_name && (
                      <span className="px-2 py-0.5 bg-purple-50 text-purple-700 rounded text-xs">{exam.subject_name}</span>
                    )}
                  </div>
                )}

                <div className="flex gap-2 mb-3">
                  <button
                    onClick={() => navigate(`/data-entry/edit-exam/${exam.id}`)}
                    className="flex-1 px-3 py-2 bg-slate-100 hover:bg-slate-200 text-slate-700 text-sm font-medium rounded-lg transition-all flex items-center justify-center gap-1"
                  >
                    <Pencil className="w-4 h-4" />
                    Edit
                  </button>
                  <button
                    onClick={() => deleteExam(exam.id, exam.title)}
                    className="px-3 py-2 bg-red-50 hover:bg-red-100 text-red-600 text-sm font-medium rounded-lg transition-all flex items-center justify-center gap-1"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>

                <p className="text-xs text-slate-400">
                  Waiting for teacher/admin to activate
                </p>
              </div>
            ))}
          </div>
        )}
      </main>
    </div>
  );
};

export default DataEntryDashboard;
